import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, vModelText as _vModelText, withDirectives as _withDirectives, normalizeClass as _normalizeClass, vModelRadio as _vModelRadio } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "edit-properties"
};
const _hoisted_2 = { class: "edit-properties__body" };
const _hoisted_3 = { class: "edit-properties__title" };
const _hoisted_4 = { class: "edit-properties__form-card" };
const _hoisted_5 = { class: "edit-properties__field" };
const _hoisted_6 = {
    for: "edit-properties-name",
    class: "edit-properties__label"
};
const _hoisted_7 = ["textContent"];
const _hoisted_8 = ["aria-invalid", "aria-describedby"];
const _hoisted_9 = { class: "edit-properties__field-row" };
const _hoisted_10 = { class: "edit-properties__field" };
const _hoisted_11 = ["textContent"];
const _hoisted_12 = ["aria-checked", "onClick"];
const _hoisted_13 = { class: "edit-properties__field" };
const _hoisted_14 = {
    for: "edit-properties-source-date",
    class: "edit-properties__label"
};
const _hoisted_15 = { class: "edit-properties__field-row" };
const _hoisted_16 = { class: "edit-properties__field" };
const _hoisted_17 = ["textContent"];
const _hoisted_18 = {
    class: "edit-properties__radio-group",
    role: "radiogroup",
    "aria-labelledby": "edit-properties-speakers-label"
};
const _hoisted_19 = ["id", "value"];
const _hoisted_20 = ["for", "textContent"];
const _hoisted_21 = ["id", "value"];
const _hoisted_22 = ["for", "textContent"];
const _hoisted_23 = {
    key: 0,
    class: "edit-properties__field"
};
const _hoisted_24 = ["textContent"];
const _hoisted_25 = ["aria-checked"];
const _hoisted_26 = ["aria-checked", "onClick"];
const _hoisted_27 = { class: "edit-properties__footer" };
const _hoisted_28 = ["disabled"];
import { computed, inject, ref, watch } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { formatFileSize, formatDurationCompact } from "@/common/helpers/format";
import { useDateTimeFormat } from "@/common/composables/date-time-format";
import { AUDIO_MIME_PREFIX, MAX_HOST_COUNT } from "../../constants";
import { ContentMode } from "@/types/external-content";
// Components
import ContentFileSummary from "../list/ContentFileSummary.vue";
import DropdownMenu from "@/common/components/DropdownMenu.vue";
import RecCalendar from "@/common/components/RecCalendar.vue";
import RecDialog from "@/common/components/RecDialog.vue";
import SvgIcon from "@/common/components/SvgIcon.vue";
import AlertMessage from "@/common/components/AlertMessage.vue";
// Setup
// `undefined` default opts out of Vue's absent-Boolean→false coercion so the content snapshot fallback survives.
export default /*@__PURE__*/ _defineComponent({
    __name: 'EditPropertiesDialog',
    props: /*@__PURE__*/ _mergeModels({
        content: {},
        languages: {},
        existingNames: {},
        saving: { type: Boolean },
        hasManualEdits: { type: Boolean, default: () => (undefined) }
    }, {
        "modelValue": { type: Boolean, ...{ required: true } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["save", "cancel"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const emit = __emit;
        const i18n = inject("i18n");
        const { toISODate } = useDateTimeFormat();
        const visible = _useModel(__props, "modelValue");
        // Local form state (cloned from content when dialog opens)
        const displayName = ref("");
        const language = ref("");
        const sourceDate = ref();
        const contentMode = ref();
        const hostCount = ref();
        const lastHostCount = ref();
        const today = ref(new Date());
        watch(visible, (next) => {
            if (next)
                today.value = new Date();
        });
        // Computed
        const fileTypeIcon = computed(() => {
            return __props.content?.mimeType?.startsWith(AUDIO_MIME_PREFIX) ? "icon-audio-file" : "icon-video-file";
        });
        const fileDisplayName = computed(() => {
            if (!__props.content)
                return "";
            const { fileName, originalFilename, displayName } = __props.content;
            return fileName || originalFilename || displayName;
        });
        const fileSummary = computed(() => {
            if (!__props.content)
                return "";
            const size = __props.content.fileSizeBytes ? formatFileSize(__props.content.fileSizeBytes) : "";
            const duration = __props.content.durationMs ? formatDurationCompact(__props.content.durationMs / 1000) : "";
            if (size && duration)
                return `${size} \u00B7 ${duration}`;
            return size || duration;
        });
        const languageDisplay = computed(() => {
            if (!language.value)
                return "";
            return __props.languages.find((l) => l.code === language.value)?.name ?? language.value;
        });
        const hostCountLabel = computed(() => {
            return hostCount.value === undefined ? i18n.t("Auto-detect") : String(hostCount.value);
        });
        // Live edit flag wins over content's snapshot — content isn't refetched after in-session edits.
        const showManualEditsWarning = computed(() => __props.hasManualEdits ?? __props.content?.hasManualEdits);
        // Switching to single-speaker has no host; stash the prior count so Multiple can restore it.
        const contentModeModel = computed({
            get: () => contentMode.value,
            set: (mode) => {
                contentMode.value = mode;
                if (mode === ContentMode.SINGLE_SPEAKER) {
                    lastHostCount.value = hostCount.value;
                    hostCount.value = undefined;
                }
                else if (mode === ContentMode.CONVERSATION) {
                    hostCount.value = lastHostCount.value;
                }
            }
        });
        const getNbHostsClass = (n) => [
            "rec-dropdown-menu__item",
            { "rec-dropdown-menu__item--active no-indicator": hostCount.value === n - 1 }
        ];
        // Validation
        const uniqueName = helpers.withMessage(i18n.t("Name must be unique."), (value) => {
            if (!value)
                return true;
            const lower = value.toLowerCase().trim();
            const currentOriginalName = __props.content?.displayName.toLowerCase().trim();
            if (lower === currentOriginalName)
                return true;
            return !(__props.existingNames ?? []).some((n) => n.toLowerCase().trim() === lower);
        });
        const requiredName = helpers.withMessage(i18n.t("Name is required"), (value) => !!value?.trim());
        const validationRules = {
            displayName: { requiredName, uniqueName }
        };
        const v$ = useVuelidate(validationRules, { displayName }, { $rewardEarly: true });
        const isFormValid = computed(() => !v$.value.$invalid);
        // Watchers - clone content data when dialog opens
        watch(visible, (val) => {
            if (val && __props.content) {
                displayName.value = __props.content.displayName;
                language.value = __props.content.language;
                contentMode.value = __props.content.contentMode;
                hostCount.value = __props.content.hostCount;
                lastHostCount.value = __props.content.hostCount;
                if (__props.content.sourceDate) {
                    const [y, m, d] = __props.content.sourceDate.split("-").map(Number);
                    sourceDate.value = new Date(y, m - 1, d);
                }
                else {
                    sourceDate.value = undefined;
                }
                v$.value.$reset();
            }
        });
        watch(displayName, () => {
            v$.value.displayName.$touch();
        });
        // Handlers
        const onCancel = () => {
            visible.value = false;
            emit("cancel");
        };
        const onSave = async () => {
            const isValid = await v$.value.$validate();
            if (!isValid || !__props.content || __props.saving)
                return;
            emit("save", __props.content.externalContentId, {
                displayName: displayName.value.trim(),
                sourceDate: sourceDate.value ? toISODate(sourceDate.value) : "",
                contentMode: contentMode.value ?? __props.content.contentMode ?? ContentMode.CONVERSATION,
                hostCount: hostCount.value,
                language: language.value || __props.content.language
            });
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(RecDialog, {
                modelValue: visible.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((visible).value = $event)),
                size: "large",
                padding: 20
            }, {
                default: _withCtx(() => [
                    (_ctx.content)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                            _createElementVNode("div", _hoisted_2, [
                                _createElementVNode("h2", _hoisted_3, _toDisplayString(_ctx.$t("Edit Properties")), 1),
                                (showManualEditsWarning.value)
                                    ? (_openBlock(), _createBlock(AlertMessage, {
                                        key: 0,
                                        type: "warning"
                                    }, {
                                        default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(_ctx.$t("Editing the language, speakers, or number of hosts will overwrite any edits made to the transcript.")), 1)
                                        ]),
                                        _: 1
                                    }))
                                    : _createCommentVNode("", true),
                                _createVNode(ContentFileSummary, {
                                    icon: fileTypeIcon.value,
                                    name: fileDisplayName.value,
                                    meta: fileSummary.value
                                }, null, 8, ["icon", "name", "meta"]),
                                _createElementVNode("div", _hoisted_4, [
                                    _createElementVNode("div", _hoisted_5, [
                                        _createElementVNode("label", _hoisted_6, _toDisplayString(_ctx.$t("Name")), 1),
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).displayName.$errors, (error) => {
                                            return (_openBlock(), _createElementBlock("span", {
                                                id: "edit-properties-name-error",
                                                key: error.$uid,
                                                class: "label label-danger",
                                                role: "alert",
                                                textContent: _toDisplayString(error.$message)
                                            }, null, 8, _hoisted_7));
                                        }), 128)),
                                        _withDirectives(_createElementVNode("input", {
                                            id: "edit-properties-name",
                                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((displayName).value = $event)),
                                            type: "text",
                                            "aria-invalid": _unref(v$).displayName.$error,
                                            "aria-describedby": _unref(v$).displayName.$error ? 'edit-properties-name-error' : undefined,
                                            class: "edit-properties__input",
                                            maxlength: "255"
                                        }, null, 8, _hoisted_8), [
                                            [_vModelText, displayName.value]
                                        ])
                                    ]),
                                    _createElementVNode("div", _hoisted_9, [
                                        _createElementVNode("div", _hoisted_10, [
                                            _createElementVNode("label", {
                                                for: "edit-properties-language",
                                                class: "edit-properties__label",
                                                textContent: _toDisplayString(_ctx.$t('Language'))
                                            }, null, 8, _hoisted_11),
                                            _createVNode(DropdownMenu, {
                                                id: "edit-properties-language",
                                                type: "select"
                                            }, {
                                                button: _withCtx(() => [
                                                    _createTextVNode(_toDisplayString(languageDisplay.value || _ctx.$t("Select...")) + " ", 1),
                                                    _createVNode(SvgIcon, {
                                                        icon: "icon-arrow-down",
                                                        class: "rec-dropdown__chevron"
                                                    })
                                                ]),
                                                menu: _withCtx(() => [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.languages, (lang) => {
                                                        return (_openBlock(), _createElementBlock("button", {
                                                            key: `edit-properties-language-${lang.code}`,
                                                            type: "button",
                                                            role: "menuitemradio",
                                                            "aria-checked": language.value === lang.code,
                                                            class: _normalizeClass([
                                                                'rec-dropdown-menu__item',
                                                                { 'rec-dropdown-menu__item--active no-indicator': language.value === lang.code }
                                                            ]),
                                                            onClick: ($event) => (language.value = lang.code)
                                                        }, _toDisplayString(lang.name), 11, _hoisted_12));
                                                    }), 128))
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _createElementVNode("div", _hoisted_13, [
                                            _createElementVNode("label", _hoisted_14, _toDisplayString(_ctx.$t("Source Date")), 1),
                                            _createVNode(RecCalendar, {
                                                id: "edit-properties-source-date",
                                                modelValue: sourceDate.value,
                                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((sourceDate).value = $event)),
                                                class: "no-highlight",
                                                "date-format": "M d, yy",
                                                "max-date": today.value,
                                                "manual-input": false,
                                                "icon-display": "input",
                                                "no-icon": ""
                                            }, null, 8, ["modelValue", "max-date"])
                                        ])
                                    ]),
                                    _createElementVNode("div", _hoisted_15, [
                                        _createElementVNode("div", _hoisted_16, [
                                            _createElementVNode("span", {
                                                id: "edit-properties-speakers-label",
                                                class: "edit-properties__label",
                                                textContent: _toDisplayString(_ctx.$t('Speakers'))
                                            }, null, 8, _hoisted_17),
                                            _createElementVNode("div", _hoisted_18, [
                                                _withDirectives(_createElementVNode("input", {
                                                    id: `session-format-${_unref(ContentMode).CONVERSATION}`,
                                                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((contentModeModel).value = $event)),
                                                    value: _unref(ContentMode).CONVERSATION,
                                                    type: "radio",
                                                    name: "session-format"
                                                }, null, 8, _hoisted_19), [
                                                    [_vModelRadio, contentModeModel.value]
                                                ]),
                                                _createElementVNode("label", {
                                                    for: `session-format-${_unref(ContentMode).CONVERSATION}`,
                                                    class: "wizard__radio-option",
                                                    textContent: _toDisplayString(_ctx.$t('Multiple'))
                                                }, null, 8, _hoisted_20),
                                                _withDirectives(_createElementVNode("input", {
                                                    id: `session-format-${_unref(ContentMode).SINGLE_SPEAKER}`,
                                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((contentModeModel).value = $event)),
                                                    value: _unref(ContentMode).SINGLE_SPEAKER,
                                                    type: "radio",
                                                    name: "session-format"
                                                }, null, 8, _hoisted_21), [
                                                    [_vModelRadio, contentModeModel.value]
                                                ]),
                                                _createElementVNode("label", {
                                                    for: `session-format-${_unref(ContentMode).SINGLE_SPEAKER}`,
                                                    class: "wizard__radio-option",
                                                    textContent: _toDisplayString(_ctx.$t('Single (no host)'))
                                                }, null, 8, _hoisted_22)
                                            ])
                                        ]),
                                        (contentMode.value === _unref(ContentMode).CONVERSATION)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                                                _createElementVNode("label", {
                                                    for: "edit-properties-host-count",
                                                    class: "edit-properties__label",
                                                    textContent: _toDisplayString(_ctx.$t('Number of Hosts'))
                                                }, null, 8, _hoisted_24),
                                                _createVNode(DropdownMenu, {
                                                    id: "edit-properties-host-count",
                                                    type: "select",
                                                    "menu-max-height": 210
                                                }, {
                                                    button: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(hostCountLabel.value) + " ", 1),
                                                        _createVNode(SvgIcon, {
                                                            icon: "icon-arrow-down",
                                                            class: "rec-dropdown__chevron"
                                                        })
                                                    ]),
                                                    menu: _withCtx(() => [
                                                        _createElementVNode("button", {
                                                            type: "button",
                                                            role: "menuitemradio",
                                                            "aria-checked": hostCount.value === undefined,
                                                            class: _normalizeClass([
                                                                'rec-dropdown-menu__item',
                                                                { 'rec-dropdown-menu__item--active no-indicator': hostCount.value === undefined }
                                                            ]),
                                                            onClick: _cache[4] || (_cache[4] = ($event) => (hostCount.value = undefined))
                                                        }, _toDisplayString(_ctx.$t("Auto-detect")), 11, _hoisted_25),
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(MAX_HOST_COUNT) + 1, (n) => {
                                                            return (_openBlock(), _createElementBlock("button", {
                                                                key: n,
                                                                type: "button",
                                                                role: "menuitemradio",
                                                                "aria-checked": hostCount.value === n - 1,
                                                                class: _normalizeClass(getNbHostsClass(n)),
                                                                onClick: ($event) => (hostCount.value = n - 1)
                                                            }, _toDisplayString(n - 1), 11, _hoisted_26));
                                                        }), 128))
                                                    ]),
                                                    _: 1
                                                })
                                            ]))
                                            : _createCommentVNode("", true)
                                    ])
                                ])
                            ]),
                            _createElementVNode("div", _hoisted_27, [
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "btn",
                                    onClick: onCancel
                                }, _toDisplayString(_ctx.$t("Cancel")), 1),
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "btn btn-primary",
                                    disabled: !isFormValid.value || _ctx.saving,
                                    onClick: onSave
                                }, _toDisplayString(_ctx.$t("Save")), 9, _hoisted_28)
                            ])
                        ]))
                        : _createCommentVNode("", true)
                ]),
                _: 1
            }, 8, ["modelValue"]));
        };
    }
});
